/**
 * ReviewImportStep
 *
 * The merged final step of the contact-import wizard. It owns a single internal
 * phase state and orchestrates the full import sequence end-to-end:
 *
 *   summary   → review counts, then "Choose destination" opens the
 *               ImportOptionsModal (destination + campaign + optional tags)
 *   importing → runs the pipeline, surfacing real status messages + progress %
 *   complete  → success screen with result stat grid + CTAs
 *
 * Import pipeline (PRESERVED exactly from the former ImportStep):
 *   markRowsCustomOther → applyTagsToRows → updateImportPreview
 *   → performSetCompanies → pollUntilIdle → performSetContacts → pollUntilIdle
 *   → re-fetch preview → count rows where CONTACT_SET != -1 as created.
 *
 * When the chosen destination is a campaign, the imported companies (distinct
 * SET_CHAIN_PROXY_ID values from the re-fetched preview) are assigned to that
 * campaign after a successful import; a failure there is non-blocking.
 *
 * Status messages map to the real sequence:
 *   "Creating companies…"  during performSetCompanies + poll
 *   "Importing contacts…"  during performSetContacts + poll
 *   "Finalizing…"          during the final re-fetch / count.
 */
import React, { Dispatch, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PiArrowCounterClockwise,
  PiCaretRight,
  PiCheckCircle,
  PiCircleNotch,
  PiCopy,
  PiDatabase,
  PiTable,
  PiAddressBook,
  PiProhibit,
  PiTag,
  PiWarning,
  PiXCircle,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { useMountEffect } from 'hooks/useMountEffect'
import useApiService from 'services/api'
import { invalidateCampaignTargetQueries } from 'services/common_table_actions'
import { InternalColumns } from 'features/importerv2/importer-types'
import {
  fetchImportPreviewData,
  performSetCompanies,
  performSetContacts,
  updateImportPreview,
} from 'services/importengine'
import { fromRowsJSON, toRowsJSON } from 'features/importerv2/ImporterViewModel'
import { markRowsCustomOther } from '../markCustomOther'
import { applyTagsToRows } from '../applyTagsToRows'
import { pollUntilIdle } from '../pollImport'
import {
  validateContactRows,
  findInFileDuplicateRowIds,
  RowIssue,
} from '../contactValidation'
import { performUndoImport, isUndoWindowExpiredError } from '../undoImport'
import { DEFAULT_COMPANY_SUBCATEGORY_SLUG } from '../contactImportFields'
import { WizardState, WizardAction } from '../wizardState'
import { ImportOptionsModal, ImportOptions } from './ImportOptionsModal'
import { StatGrid, StatItem } from './StatGrid'

// ---------------------------------------------------------------------------
// Phase + status constants
// ---------------------------------------------------------------------------

export type Phase = 'summary' | 'importing' | 'complete'

/** Status messages shown during the importing phase, in pipeline order. */
const STATUS = {
  preparing: 'Preparing import…',
  companies: 'Creating companies…',
  contacts: 'Importing contacts…',
  finalizing: 'Finalizing…',
} as const

/**
 * Seconds the undo affordance stays available after the import completes.
 * The backend allows a slightly longer grace window (15 min) — see
 * `server/importengine/service/v2/undo_import.py` (UNDO_WINDOW).
 */
const UNDO_WINDOW_SECONDS = 600

/** Format a number of seconds as m:ss (e.g. 9:05). */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// InlineAlert
// ---------------------------------------------------------------------------

/**
 * Reusable alert banner for inline error and loading messages within the step.
 *
 * @param variant - 'error' shows red border/bg; 'info' shows muted border/bg.
 * @param children - Alert message content.
 */
function InlineAlert({
  variant,
  children,
}: {
  variant: 'error' | 'info'
  children: React.ReactNode
}) {
  return (
    <Alert
      color={variant === 'error' ? 'danger' : 'default'}
      icon={null}
      description={children}
    />
  )
}

// ---------------------------------------------------------------------------
// UndoControl — the complete-phase "Undo this import (m:ss)" affordance.
// ---------------------------------------------------------------------------

type UndoStatus = 'idle' | 'pending' | 'done' | 'expired'

/**
 * Renders the undo affordance for the complete phase:
 * - Counts down from `UNDO_WINDOW_SECONDS` (mm:ss); at 0:00 the window closes
 *   and the button is hidden.
 * - On click, calls `performUndoImport(importNumber)`, shows a pending state,
 *   then "Import undone." on success.
 * - A backend 409 (window expired) is surfaced as an inline message.
 *
 * Rendered only in the complete phase and keyed to remount per import, so the
 * one allowed `useMountEffect` cleanly sets up and tears down its interval.
 */
function UndoControl({
  importNumber,
  onUndone,
}: {
  importNumber: number
  onUndone: () => void
}) {
  const [secondsLeft, setSecondsLeft] = useState(UNDO_WINDOW_SECONDS)
  const [status, setStatus] = useState<UndoStatus>('idle')

  useMountEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval)
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  })

  const handleUndo = async () => {
    if (status === 'pending' || status === 'done') return
    setStatus('pending')
    try {
      await performUndoImport(importNumber)
      setStatus('done')
      onUndone()
    } catch (err) {
      // Window expired server-side (409) — surface inline; the countdown may
      // not have hit 0 locally due to clock skew / latency.
      setStatus(isUndoWindowExpiredError(err) ? 'expired' : 'idle')
    }
  }

  if (status === 'done') {
    return <p className="text-xs text-fg-secondary">Import undone.</p>
  }

  if (status === 'expired') {
    return (
      <p className="text-xs text-fg-secondary">
        The undo window has expired — this import can no longer be undone.
      </p>
    )
  }

  // Window closed: hide the affordance entirely.
  if (secondsLeft <= 0) return null

  return (
    <button
      type="button"
      onClick={handleUndo}
      disabled={status === 'pending'}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-fg-secondary transition-colors',
        status === 'pending'
          ? 'cursor-not-allowed opacity-70'
          : 'hover:text-error-strong'
      )}
    >
      {status === 'pending' ? (
        <>
          <PiCircleNotch className="size-3 animate-spin" />
          Undoing import…
        </>
      ) : (
        <>
          <PiArrowCounterClockwise className="size-3" />
          Undo this import
          <span className="ml-0.5 tabular-nums">
            ({formatCountdown(secondsLeft)})
          </span>
        </>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// ReviewImportStep
// ---------------------------------------------------------------------------

interface ReviewImportStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
  /** Reports the internal phase up so the wizard can hide its header once the
   *  import is underway/complete (the success screen owns its own heading). */
  onPhaseChange?: (phase: Phase) => void
}

export function ReviewImportStep({
  state,
  dispatch,
  onPhaseChange,
}: ReviewImportStepProps) {
  const navigate = useNavigate()
  const api = useApiService()
  const { data: companyCategories, isLoading: isCategoriesLoading } =
    api.useGetCompanyCategories()

  // Derive the "Other/Other" subcategory used for custom companies.
  const otherType = useMemo(() => {
    if (!companyCategories) return undefined
    for (const cat of companyCategories) {
      for (const sub of cat.sub_categories ?? []) {
        if (sub.slug === DEFAULT_COMPANY_SUBCATEGORY_SLUG) {
          return { category_id: cat.id, value: sub.id }
        }
      }
    }
    return undefined
  }, [companyCategories])

  // Local phase / progress state.
  const [phase, setPhase] = useState<Phase>('summary')
  // Set the phase and report it up so the wizard can hide its header off-summary.
  const goToPhase = (next: Phase) => {
    setPhase(next)
    onPhaseChange?.(next)
  }
  const [progress, setProgress] = useState(0)
  const [statusMsg, setStatusMsg] = useState<string>(STATUS.preparing)
  const [error, setError] = useState<string | null>(null)
  // The "Choose destination" modal (destination + campaign + tags lives there).
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  // The campaign the contacts were imported into, when destination === campaign.
  // Drives the complete-phase summary line + the "View campaign" CTA. Null for
  // CRM-only imports.
  const [importedCampaignName, setImportedCampaignName] = useState<
    string | null
  >(null)
  const [importedCampaignId, setImportedCampaignId] = useState<number | null>(
    null
  )
  // Tag names applied during the import — shown as chips on the success screen.
  const [importedTagNames, setImportedTagNames] = useState<string[]>([])
  // True when the import succeeded but assigning the imported companies to the
  // chosen campaign failed — surfaced as a non-blocking note (contacts are in).
  // True once the user has undone this import — contacts are gone, so the
  // success copy and CTAs swap to a "start over" framing.
  const [undone, setUndone] = useState(false)
  // Set when an import fails AFTER the backend pipeline was triggered. The
  // Celery task may still be running, so re-running could double-process — we
  // lock the import and tell the user to refresh and check, rather than offer a
  // clean retry. Errors before the pipeline starts leave this false (safe retry).
  const [importLocked, setImportLocked] = useState(false)

  // isRunning ref — updated in the click handler (no effect needed).
  const isRunningRef = useRef(false)
  // Tracks whether the backend pipeline (set-companies onward) was triggered in
  // the current run — used by the catch handler to decide lock vs. retry.
  const pipelineStartedRef = useRef(false)

  // Navigation guard: register once on mount, read isRunningRef in the handler.
  useMountEffect(() => {
    // On (re)mount the step always starts on the summary phase — tell the wizard
    // so it shows the header again after stepper navigation back into Review.
    onPhaseChange?.('summary')

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isRunningRef.current) return
      e.preventDefault()
    }

    const handlePopState = (e: PopStateEvent) => {
      if (!isRunningRef.current) return
      e.preventDefault()
      const leave = window.confirm(
        'Import is in progress. Are you sure you want to leave? This may cause data loss.'
      )
      if (!leave) {
        window.history.pushState(null, '', window.location.href)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  })

  // ---------------------------------------------------------------------------
  // Derived counts
  // ---------------------------------------------------------------------------

  const totalRows = state.importData?.rows.length ?? 0
  const includedCount = totalRows - state.excludedRowIds.size
  const excludedCount = state.excludedRowIds.size

  const includedRowIds: ReadonlySet<string> = useMemo(() => {
    if (!state.importData) return new Set<string>()
    const ids = new Set<string>()
    for (const row of state.importData.rows) {
      const id = String(row[InternalColumns.ROW_ID])
      if (!state.excludedRowIds.has(id)) {
        ids.add(id)
      }
    }
    return ids
  }, [state.importData, state.excludedRowIds])

  // Validation-derived counts (recomputed from rows — mirrors ValidateStep, no
  // useEffect). Used to populate the summary stat grid (errors / duplicates).
  const { errorCount, duplicateCount } = useMemo(() => {
    if (!state.importData) return { errorCount: 0, duplicateCount: 0 }
    const { rows, headers } = state.importData
    const issues: Record<string, RowIssue[]> = validateContactRows(
      rows,
      headers
    )
    let errors = 0
    for (const rowIssues of Object.values(issues)) {
      if (rowIssues.some((i) => i.severity === 'error')) errors++
    }
    // Duplicates = the same union the Validate tab shows: in-file duplicates
    // (same email on 2+ rows) ∪ CRM duplicates (IS_EXISTING_CRM_CONTACT).
    // validateContactRows does NOT emit a "Duplicate" error, so deriving this
    // from issues would always be 0.
    const duplicateIds = new Set<string>(
      findInFileDuplicateRowIds(rows, headers)
    )
    for (const r of rows) {
      if (r[InternalColumns.IS_EXISTING_CRM_CONTACT] === true) {
        duplicateIds.add(String(r[InternalColumns.ROW_ID]))
      }
    }
    return { errorCount: errors, duplicateCount: duplicateIds.size }
  }, [state.importData])

  // ---------------------------------------------------------------------------
  // Import pipeline (preserved byte-for-byte; only status messages added)
  // ---------------------------------------------------------------------------

  /**
   * Runs the full import pipeline with the options chosen in the modal.
   *
   * Tags from the modal are applied via the existing `applyTagsToRows` path
   * (unchanged). When the destination is a campaign, the backend assigns the
   * imported companies to it (and writes any custom columns) atomically during
   * set-contacts.
   */
  const handleStartImport = async (opts: ImportOptions) => {
    if (!state.importData || !state.importNumber || !otherType) return
    if (phase === 'importing') return

    const tagNames = opts.tagNames
    setImportedCampaignName(null)
    setImportedCampaignId(null)
    setImportedTagNames([])

    goToPhase('importing')
    setError(null)
    setProgress(0)
    setStatusMsg(STATUS.preparing)
    isRunningRef.current = true
    pipelineStartedRef.current = false

    try {
      // Step a: mark all rows as custom-Other, set READY flags.
      const marked = markRowsCustomOther(
        state.importData,
        state.excludedRowIds,
        otherType
      )

      // Step b: inject tag names into contact_label column (if any).
      const withTags =
        tagNames.length > 0
          ? applyTagsToRows(marked, includedRowIds, tagNames)
          : marked

      // Step c: persist updated rows to the backend.
      await updateImportPreview(state.importNumber, {
        import_data: { data: toRowsJSON(withTags) },
      })

      // Step d: trigger set-companies then poll until idle. From here on the
      // backend pipeline is in flight — a failure must lock (not offer retry).
      pipelineStartedRef.current = true
      setStatusMsg(STATUS.companies)
      setProgress(30)
      await performSetCompanies(state.importNumber)
      await pollUntilIdle(state.importNumber)

      // Step e: trigger set-contacts then poll until idle. When importing to a
      // campaign (locked entry OR campaign chosen in the modal), send its id so
      // the backend assigns the companies AND writes any custom campaign-column
      // values atomically — custom columns are campaign-scoped, so they only
      // get written when set-contacts receives a campaign_id.
      const campaignIdForImport =
        opts.destination === 'campaign' ? opts.campaignId : null
      setStatusMsg(STATUS.contacts)
      setProgress(65)
      await performSetContacts(
        state.importNumber,
        state.existingContactMode,
        campaignIdForImport,
        state.customColumns
      )
      await pollUntilIdle(state.importNumber)

      // Step f: re-fetch the preview to get accurate created/failed counts.
      // The backend writes the created Contact id into CONTACT_SET for each row
      // it successfully imports, leaving -1 for rows it skipped or failed.
      setStatusMsg(STATUS.finalizing)
      setProgress(95)
      let created = includedCount // fallback: assume all succeeded
      try {
        const finalPreview = await fetchImportPreviewData(state.importNumber)
        const finalData = fromRowsJSON(finalPreview.data)
        created = finalData.rows.filter(
          (r) =>
            r[InternalColumns.CONTACT_SET] !== -1 &&
            r[InternalColumns.CONTACT_SET] != null
        ).length
      } catch {
        // Re-fetch failed — fall back to optimistic count (created = includedCount).
      }
      const failed = Math.max(0, includedCount - created)
      setProgress(100)

      // Step g: compute result counts.
      const result = {
        created,
        excluded: excludedCount,
        failed,
      }

      // Step h: store result; the contacts are now imported regardless of what
      // happens with the campaign assignment below.
      dispatch({ type: 'SET_RESULT', result })

      // Record the applied tags for the success screen (only when contacts
      // actually imported — if everything was excluded, no tags were applied).
      if (created > 0 && tagNames.length > 0) {
        setImportedTagNames(tagNames)
      }

      // Step i: record the campaign for the success screen. The backend already
      // assigned the imported companies to the campaign (and wrote any custom
      // columns) via set-contacts' campaign_id, for both the locked entry and
      // the Contacts-page path. Only when at least one contact was actually
      // imported — if every row was excluded (created === 0) nothing was
      // assigned, so don't claim a campaign on the success screen.
      if (
        opts.destination === 'campaign' &&
        opts.campaignId != null &&
        created > 0
      ) {
        setImportedCampaignName(opts.campaignName)
        setImportedCampaignId(opts.campaignId)
        // Drop cached campaign-scoped queries so the "View campaign" CTA lands
        // on fresh tables/counts (FIR-7460).
        invalidateCampaignTargetQueries(opts.campaignId)
      }

      goToPhase('complete')
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'An unexpected error occurred during import.'
      goToPhase('summary')
      if (pipelineStartedRef.current) {
        // The import was already triggered on the backend and the task may
        // still be running. Lock the flow so a retry can't double-process —
        // the user should refresh to see whether the import actually finished.
        setImportLocked(true)
        setError(
          'The import started but did not confirm completion. Refresh the page to check whether your contacts were imported before trying again.'
        )
      } else {
        setError(msg)
      }
    } finally {
      isRunningRef.current = false
    }
  }

  // ---------------------------------------------------------------------------
  // Derived render flags
  // ---------------------------------------------------------------------------

  const isReady = !isCategoriesLoading && !!otherType
  const canStart =
    isReady && includedCount > 0 && phase !== 'importing' && !importLocked
  const result = state.result

  // ---------------------------------------------------------------------------
  // Summary phase stat grid
  // ---------------------------------------------------------------------------

  const summaryStats: StatItem[] = [
    {
      icon: <PiTable className="size-6 text-ds2026-badge-success-icon" />,
      value: totalRows,
      label: 'Total rows',
      bg: 'bg-surface',
      textColor: 'text-fg',
      iconBg: 'bg-success-bg-tint',
    },
    {
      // Included = the positive metric, so its icon tile gets the success-green
      // treatment (green-100 fill + green-600 glyph). Card surface/value stay
      // neutral like the other summary cards.
      icon: <PiAddressBook className="size-6 text-ds2026-badge-success-icon" />,
      value: includedCount,
      label: 'Included',
      bg: 'bg-surface',
      textColor: 'text-fg',
      iconBg: 'bg-success-bg-tint',
    },
    {
      icon: <PiProhibit className="size-6 text-fg-tertiary" />,
      value: excludedCount,
      label: 'Excluded',
      bg: 'bg-surface',
      textColor: 'text-fg',
    },
    {
      icon: <PiCopy className="size-6 text-fg-tertiary" />,
      value: duplicateCount,
      label: 'Duplicates',
      bg: 'bg-surface',
      textColor: 'text-fg',
    },
    {
      icon: <PiWarning className="size-6 text-fg-tertiary" />,
      value: errorCount,
      label: 'Errors',
      bg: 'bg-surface',
      textColor: 'text-fg',
    },
  ]

  const createdCount = result?.created ?? 0
  const excludedResultCount = result?.excluded ?? 0
  // The pipeline ran but nothing actually landed (every row excluded or failed).
  // The complete screen swaps to a neutral "nothing imported" treatment instead
  // of the green success celebration.
  const noneImported = createdCount === 0
  const completeStats: StatItem[] = [
    {
      // Green success treatment only when contacts actually imported; otherwise
      // the card stays neutral so a "0 imported" outcome doesn't read as a win.
      icon: (
        <PiAddressBook
          className={cn(
            'size-6',
            noneImported ? 'text-fg-tertiary' : 'text-ds2026-badge-success-icon'
          )}
        />
      ),
      value: createdCount,
      label: `${createdCount === 1 ? 'Contact' : 'Contacts'} imported`,
      // Success treatment, all on the Tailwind GREEN scale (not emerald) so every
      // green reads as one family and matches the header circle:
      //   card bg  green-50  (badge-success-bg)
      //   tile bg  green-100 (success-bg-tint) — same fill as the header circle
      //   icon     green-600 (badge-success-icon) — same glyph color as circle
      //   value +  green-700 (badge-success-text)
      //   border   bright success green (success-bright)
      bg: noneImported ? 'bg-surface' : 'bg-ds2026-badge-success-bg',
      textColor: noneImported ? 'text-fg' : 'text-ds2026-badge-success-text',
      labelColor: noneImported ? undefined : 'text-ds2026-badge-success-text',
      iconBg: noneImported ? undefined : 'bg-success-bg-tint',
      border: noneImported ? undefined : 'border-success-bright',
    },
    {
      icon: <PiProhibit className="size-6 text-fg-tertiary" />,
      value: excludedResultCount,
      label: `${excludedResultCount === 1 ? 'Row' : 'Rows'} excluded`,
      bg: 'bg-surface',
      textColor: 'text-fg',
    },
    {
      icon: <PiXCircle className="size-6 text-fg-tertiary" />,
      value: result?.failed ?? 0,
      label: 'Failed',
      bg: 'bg-surface',
      textColor: 'text-fg',
    },
  ]

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {showOptionsModal && (
        <ImportOptionsModal
          lockedCampaignId={state.campaignId}
          hasCustomColumns={state.customColumns.length > 0}
          onClose={() => setShowOptionsModal(false)}
          onConfirm={(opts) => {
            setShowOptionsModal(false)
            void handleStartImport(opts)
          }}
        />
      )}

      <div className="absolute inset-0 flex flex-col">
        <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-5">
          {/* ── Summary phase ── */}
          {phase === 'summary' && (
            <>
              {/* Stat grid */}
              <StatGrid items={summaryStats} />

              {/* What Happens Next */}
              <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3.5">
                <p className="text-xs font-semibold text-fg-secondary uppercase tracking-wide mb-2.5">
                  What happens next
                </p>
                <ul className="flex flex-col gap-2">
                  <li className="flex items-start gap-2.5">
                    <PiAddressBook className="size-3.5 text-fg-tertiary mt-0.5 shrink-0" />
                    <p className="text-sm text-fg-secondary">
                      <span className="font-medium text-fg">
                        {includedCount.toLocaleString()} contact
                        {includedCount !== 1 ? 's' : ''} will be added to First
                        Bite
                      </span>{' '}
                      and linked to their companies.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <PiDatabase className="size-3.5 text-fg-tertiary mt-0.5 shrink-0" />
                    <p className="text-sm text-fg-secondary">
                      Companies that don&rsquo;t match an existing First Bite
                      company will be created as custom companies under the
                      &ldquo;Other&rdquo; category.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <PiTag className="size-3.5 text-fg-tertiary mt-0.5 shrink-0" />
                    <p className="text-sm text-fg-secondary">
                      Choose a destination and any optional contact labels on
                      the next step — labels are applied to every imported
                      contact.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <PiArrowCounterClockwise className="size-3.5 text-fg-tertiary mt-0.5 shrink-0" />
                    <p className="text-sm text-fg-secondary">
                      You can undo this import shortly after it finishes.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Categories loading / missing warning */}
              {isCategoriesLoading && (
                <InlineAlert variant="info">
                  <span className="flex items-center gap-2">
                    <PiCircleNotch className="size-4 animate-spin shrink-0" />
                    Loading company type data...
                  </span>
                </InlineAlert>
              )}

              {!isCategoriesLoading && !otherType && (
                <InlineAlert variant="error">
                  Unable to resolve the default company type. Please refresh and
                  try again.
                </InlineAlert>
              )}

              {/* Runtime error */}
              {error && <InlineAlert variant="error">{error}</InlineAlert>}
            </>
          )}

          {/* ── Importing phase ── */}
          {phase === 'importing' && (
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="size-16 rounded-full bg-success-bg-subtle flex items-center justify-center">
                <PiCircleNotch className="size-8 text-brand-bright animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-fg">
                  Importing {includedCount.toLocaleString()} contact
                  {includedCount !== 1 ? 's' : ''}...
                </p>
                <p
                  className="text-sm text-fg-secondary mt-1"
                  aria-live="polite"
                >
                  {statusMsg}
                </p>
              </div>
              <div className="w-full max-w-sm">
                <div
                  className="h-2.5 rounded-full bg-line overflow-hidden"
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full bg-brand-bright rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-1.5">
                  <span className="text-xs text-fg-secondary">{progress}%</span>
                </div>
              </div>
              <p className="text-xs text-fg-secondary">
                Please keep this page open until the import completes.
              </p>
            </div>
          )}

          {/* ── Complete phase ── */}
          {phase === 'complete' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              {/* Status icon. Success → green checkmark (green-100 fill +
                  green-600 glyph). Nothing-imported → amber warning, so a 0-row
                  outcome doesn't read as a celebration. */}
              {!undone && noneImported ? (
                <div className="size-16 rounded-full bg-warning-bg-subtle flex items-center justify-center">
                  <PiWarning className="size-9 text-warning-strong" />
                </div>
              ) : (
                <div className="size-16 rounded-full bg-success-bg-tint flex items-center justify-center">
                  <PiCheckCircle className="size-9 text-ds2026-badge-success-icon" />
                </div>
              )}

              {/* Heading — this is the page header (wizard header is hidden here) */}
              <h3 className="mt-4 text-xl font-bold text-fg">
                {undone
                  ? 'Import undone'
                  : noneImported
                    ? 'No contacts imported'
                    : 'Import success!'}
              </h3>

              {/* Result stat grid — hidden once undone (counts no longer apply). */}
              {!undone && (
                <StatGrid
                  items={completeStats}
                  size="lg"
                  className="mt-6 w-full max-w-lg"
                />
              )}

              {/* Detail line — 16px above & below. */}
              <p className="my-4 text-sm text-fg-secondary">
                {undone ? (
                  'The imported contacts and any companies created during this import have been removed from First Bite.'
                ) : noneImported ? (
                  'None of your rows were imported — every row was either excluded or failed to import. Go back to review your data and try again.'
                ) : (
                  <>
                    <span className="font-semibold text-fg">
                      {createdCount.toLocaleString()} contact
                      {createdCount !== 1 ? 's' : ''}
                    </span>{' '}
                    {createdCount !== 1 ? 'have' : 'has'} been added to First
                    Bite
                    {importedCampaignName ? (
                      <>
                        {' '}
                        and added to{' '}
                        <button
                          type="button"
                          onClick={() => {
                            const cid = state.campaignId ?? importedCampaignId
                            if (cid != null) {
                              navigate(`/campaigns/${cid}/detail`)
                            }
                          }}
                          className="font-semibold text-brand-bright transition-colors hover:text-brand-active"
                        >
                          {importedCampaignName}
                        </button>
                      </>
                    ) : null}
                    .
                  </>
                )}
              </p>

              {/* Applied contact labels — shown as chips when labels were added during import. */}
              {!undone && importedTagNames.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-sm text-fg-secondary">
                  <span>
                    <span className="font-semibold text-fg">
                      {importedTagNames.length}
                    </span>{' '}
                    contact label{importedTagNames.length !== 1 ? 's' : ''}{' '}
                    added:
                  </span>
                  {importedTagNames.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-sunken px-2.5 py-1 text-xs font-medium text-fg"
                    >
                      <PiTag className="size-3 shrink-0 text-fg-tertiary" />
                      {name}
                    </span>
                  ))}
                </div>
              )}

              {/* CTAs — secondary "Import another" (left) → primary "View
                  contacts" (right). The campaign is reachable via the inline
                  link on its name in the summary above, so there's no separate
                  "View campaign" button. */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {undone ? (
                  <FbButton
                    variant="primary"
                    onClick={() => dispatch({ type: 'RESET' })}
                  >
                    Import another
                    <PiCaretRight className="size-4" />
                  </FbButton>
                ) : noneImported ? (
                  <>
                    <FbButton
                      variant="secondary"
                      onClick={() => dispatch({ type: 'RESET' })}
                    >
                      Import another
                    </FbButton>
                    <FbButton
                      variant="primary"
                      onClick={() =>
                        dispatch({ type: 'SET_STEP', step: 'validate' })
                      }
                    >
                      Back to review
                      <PiCaretRight className="size-4" />
                    </FbButton>
                  </>
                ) : (
                  <>
                    <FbButton
                      variant="secondary"
                      onClick={() => dispatch({ type: 'RESET' })}
                    >
                      Import another
                    </FbButton>
                    <FbButton
                      variant="primary"
                      onClick={() => {
                        const cid = state.campaignId ?? importedCampaignId
                        if (cid != null) {
                          navigate(`/campaigns/${cid}/detail?tab=contacts`)
                        } else {
                          navigate('/contacts/all')
                        }
                      }}
                    >
                      View contacts
                    </FbButton>
                  </>
                )}
              </div>

              {/* Undo affordance — 10-min countdown, keyed to remount per import.
                  Hidden when nothing imported (there's nothing to undo). */}
              {!undone && !noneImported && state.importNumber != null && (
                <div className="mt-6">
                  <UndoControl
                    key={`undo-${state.importNumber}`}
                    importNumber={state.importNumber}
                    onUndone={() => setUndone(true)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — summary phase only (no nav during importing/complete) */}
        {phase === 'summary' && (
          <div className="px-8 py-4 border-t border-line bg-surface flex items-center justify-between shrink-0">
            <FbButton
              variant="secondary"
              onClick={() => dispatch({ type: 'SET_STEP', step: 'validate' })}
            >
              Back
            </FbButton>
            <FbButton
              variant="primary"
              onClick={() => setShowOptionsModal(true)}
              disabled={!canStart}
            >
              Choose destination
              <PiCaretRight className="size-4" />
            </FbButton>
          </div>
        )}
      </div>
    </>
  )
}
